import { useState, useEffect } from 'react'
import { 
  Autocomplete,
  TextField,
  CircularProgress, 
  Alert,
  Box,
  Typography
} from '@mui/material'
import { secretsApi } from '../services/api'

interface Secret {
  name: string
  fp_code: string
  system: string
  display_name: string
}

interface SecretSelectorProps {
  system: 'lightspeed' | 'skimmer'
  value: string
  onChange: (value: string, secret: Secret | null) => void
  label?: string
}

function SecretSelector({ system, value, onChange, label = 'Select Franchise Partner' }: SecretSelectorProps) {
  const [secrets, setSecrets] = useState<Secret[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadSecrets()
  }, [system])

  const loadSecrets = async () => {
    setLoading(true)
    setError(null)
    try {
      const api = system === 'lightspeed' ? secretsApi.listLightspeed : secretsApi.listSkimmer
      const response = await api()
      setSecrets(response.data)
    } catch (err) {
      setError('Failed to load franchise partners')
      console.error('Error loading secrets:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (_event: any, newValue: Secret | null) => {
    if (newValue) {
      onChange(newValue.fp_code, newValue)
    } else {
      onChange('', null)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  const selectedSecret = secrets.find(s => s.fp_code === value) || null

  const highlightMatch = (text: string, query: string) => {
    if (!query) return <Typography component="span">{text}</Typography>
    
    const index = text.toLowerCase().indexOf(query.toLowerCase())
    if (index === -1) return <Typography component="span">{text}</Typography>
    
    return (
      <Typography component="span">
        {text.substring(0, index)}
        <Box component="span" sx={{ backgroundColor: 'yellow', fontWeight: 600 }}>
          {text.substring(index, index + query.length)}
        </Box>
        {text.substring(index + query.length)}
      </Typography>
    )
  }

  return (
    <Autocomplete
      fullWidth
      options={secrets}
      value={selectedSecret}
      onChange={handleChange}
      getOptionLabel={(option) => option.display_name}
      isOptionEqualToValue={(option, value) => option.fp_code === value?.fp_code}
      renderOption={(props, option, { inputValue }) => {
        const { key, ...otherProps } = props
        return (
          <Box component="li" key={key} {...otherProps}>
            {highlightMatch(option.display_name, inputValue)}
          </Box>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': {
                borderColor: 'info.main',
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'info.main',
            },
          }}
        />
      )}
      loading={loading}
      loadingText="Loading franchise partners..."
      noOptionsText="No franchise partners found"
      clearText="Clear selection"
    />
  )
}

export default SecretSelector